import { useRef, useState } from "react";
import {
  Bold,
  CheckSquare,
  Eye,
  Italic,
  List,
  ListOrdered,
  Pencil,
  Table,
  Underline,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { MarkdownView } from "@/lib/markdown";
import { cn } from "@/lib/utils";

/**
 * Controlled markdown notes editor: a textarea with an Edit/Preview toggle and
 * a small formatting toolbar (B / I / U / bullet / numbered / checkbox) that
 * wraps the current selection (or inserts a starter when nothing's selected).
 *
 * Presentation only — the caller owns the value and persistence. CRO Forecast
 * wraps this with its debounced auto-save; Proposals stores the value on the
 * proposal itself and saves it with the proposal. Markdown (incl. GFM tables)
 * is rendered by {@link MarkdownView}.
 */
export function MarkdownNotes({
  value,
  onChange,
  label,
  placeholder,
  minHeightPx = 80,
  readOnly = false,
  initialMode,
  statusText = "Markdown",
  className,
}: {
  value: string;
  onChange: (next: string) => void;
  label?: string;
  placeholder?: string;
  minHeightPx?: number;
  readOnly?: boolean;
  /**
   * Force the starting mode. When omitted, falls back to the heuristic below.
   * Read-only always wins (preview). Only read at mount — pass a changing
   * `key` if the caller needs it to re-apply.
   */
  initialMode?: "edit" | "preview";
  /** Right-aligned hint in the toolbar (e.g. "Saving…"). Defaults to "Markdown". */
  statusText?: string;
  className?: string;
}) {
  const [focused, setFocused] = useState(false);
  // Read-only notes are always preview. Otherwise honor an explicit initialMode;
  // when unset, default to preview if the note already has content (most of the
  // time the user is reading, not editing) and edit for an empty note.
  const [mode, setMode] = useState<"edit" | "preview">(() =>
    readOnly ? "preview" : initialMode ?? (value.trim() ? "preview" : "edit"),
  );
  const taRef = useRef<HTMLTextAreaElement | null>(null);

  function wrapSelection(prefix: string, suffix: string = prefix) {
    const ta = taRef.current;
    if (!ta) return;
    const start = ta.selectionStart;
    const end = ta.selectionEnd;
    const before = value.slice(0, start);
    const selected = value.slice(start, end);
    const after = value.slice(end);
    const inserted = selected || "text";
    onChange(`${before}${prefix}${inserted}${suffix}${after}`);
    // Reposition selection inside the inserted segment.
    requestAnimationFrame(() => {
      ta.focus();
      const cursorStart = start + prefix.length;
      const cursorEnd = cursorStart + inserted.length;
      ta.setSelectionRange(cursorStart, cursorEnd);
    });
  }

  function insertLinePrefix(prefix: string) {
    const ta = taRef.current;
    if (!ta) return;
    const start = ta.selectionStart;
    const end = ta.selectionEnd;
    const before = value.slice(0, start);
    const selected = value.slice(start, end);
    const after = value.slice(end);
    // Multi-line (or any) selection: prefix every line; otherwise prefix the
    // current line (find its start and insert there).
    if (selected.includes("\n") || selected.length > 0) {
      const lines = (selected || "item").split("\n");
      onChange(`${before}${lines.map((l) => `${prefix}${l}`).join("\n")}${after}`);
    } else {
      const lineStart = before.lastIndexOf("\n") + 1;
      onChange(`${value.slice(0, lineStart)}${prefix}${value.slice(lineStart)}`);
      requestAnimationFrame(() => {
        ta.focus();
        const newPos = start + prefix.length;
        ta.setSelectionRange(newPos, newPos);
      });
    }
  }

  function insertBlock(block: string) {
    const ta = taRef.current;
    if (!ta) return;
    const start = ta.selectionStart;
    const before = value.slice(0, start);
    const after = value.slice(start);
    // Pad with blank lines so the block parses as its own element, but don't
    // stack extra newlines when the cursor is already on a fresh line.
    const lead = before === "" || before.endsWith("\n\n") ? "" : before.endsWith("\n") ? "\n" : "\n\n";
    const trail = after === "" || after.startsWith("\n") ? "" : "\n";
    const insert = `${lead}${block}${trail}`;
    onChange(`${before}${insert}${after}`);
    requestAnimationFrame(() => {
      ta.focus();
      const pos = start + insert.length;
      ta.setSelectionRange(pos, pos);
    });
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    // Ctrl/Cmd shortcuts mirror the toolbar buttons.
    if (!(e.metaKey || e.ctrlKey)) return;
    const key = e.key.toLowerCase();
    if (key === "b") {
      e.preventDefault();
      wrapSelection("**");
    } else if (key === "i") {
      e.preventDefault();
      wrapSelection("*");
    } else if (key === "u") {
      e.preventDefault();
      wrapSelection("<u>", "</u>");
    }
  }

  return (
    <div
      className={cn(
        "space-y-1.5 rounded-lg border bg-muted/30 p-3 transition-colors",
        focused && mode === "edit" && "border-power/40 bg-card",
        className,
      )}
    >
      <div className="flex items-center justify-between">
        {label ? (
          <label className="text-[10px] uppercase tracking-wider text-muted-foreground">
            {label}
          </label>
        ) : (
          <span />
        )}
        {!readOnly && (
          <div className="inline-flex rounded-md border bg-card p-0.5">
            <button
              type="button"
              onClick={() => setMode("edit")}
              className={cn(
                "inline-flex items-center gap-1 rounded-sm px-2 py-0.5 text-[10px] uppercase tracking-wider transition-colors",
                mode === "edit"
                  ? "bg-power text-white"
                  : "text-muted-foreground hover:text-foreground",
              )}
              title="Edit markdown source"
            >
              <Pencil className="h-3 w-3" />
              Edit
            </button>
            <button
              type="button"
              onClick={() => setMode("preview")}
              className={cn(
                "inline-flex items-center gap-1 rounded-sm px-2 py-0.5 text-[10px] uppercase tracking-wider transition-colors",
                mode === "preview"
                  ? "bg-power text-white"
                  : "text-muted-foreground hover:text-foreground",
              )}
              title="Render markdown preview"
            >
              <Eye className="h-3 w-3" />
              Preview
            </button>
          </div>
        )}
      </div>

      {mode === "edit" && !readOnly ? (
        <>
          <Textarea
            ref={taRef}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            onFocus={() => setFocused(true)}
            onBlur={() => setFocused(false)}
            onKeyDown={handleKeyDown}
            placeholder={placeholder ?? "Add notes…"}
            style={{ minHeight: minHeightPx }}
            className="resize-y border-none bg-transparent shadow-none focus-visible:ring-0"
          />
          {focused && (
            <div
              className="flex items-center gap-0.5 border-t pt-1.5"
              onMouseDown={(e) => e.preventDefault()} // keep textarea focus
            >
              <TbBtn title="Bold (Ctrl+B)" onClick={() => wrapSelection("**")}>
                <Bold className="h-3.5 w-3.5" />
              </TbBtn>
              <TbBtn title="Italic (Ctrl+I)" onClick={() => wrapSelection("*")}>
                <Italic className="h-3.5 w-3.5" />
              </TbBtn>
              <TbBtn title="Underline (Ctrl+U)" onClick={() => wrapSelection("<u>", "</u>")}>
                <Underline className="h-3.5 w-3.5" />
              </TbBtn>
              <span className="mx-1 h-4 w-px bg-border" />
              <TbBtn title="Bullet list" onClick={() => insertLinePrefix("- ")}>
                <List className="h-3.5 w-3.5" />
              </TbBtn>
              <TbBtn title="Numbered list" onClick={() => insertLinePrefix("1. ")}>
                <ListOrdered className="h-3.5 w-3.5" />
              </TbBtn>
              <TbBtn title="Checkbox" onClick={() => insertLinePrefix("- [ ] ")}>
                <CheckSquare className="h-3.5 w-3.5" />
              </TbBtn>
              <span className="mx-1 h-4 w-px bg-border" />
              <TbBtn
                title="Table"
                onClick={() =>
                  insertBlock(
                    "| Column | Column |\n| --- | --- |\n| Cell | Cell |\n| Cell | Cell |",
                  )
                }
              >
                <Table className="h-3.5 w-3.5" />
              </TbBtn>
              <span className="ml-auto text-[10px] text-muted-foreground">{statusText}</span>
            </div>
          )}
        </>
      ) : (
        <div
          className={cn("rounded p-1 -m-1", !readOnly && "cursor-text hover:bg-muted/40")}
          style={{ minHeight: minHeightPx }}
          onClick={readOnly ? undefined : () => setMode("edit")}
          title={readOnly ? undefined : "Click to edit"}
        >
          {value.trim() ? (
            <MarkdownView source={value} />
          ) : (
            <span className="text-sm text-muted-foreground">{placeholder ?? "No notes."}</span>
          )}
        </div>
      )}
    </div>
  );
}

function TbBtn({
  title,
  onClick,
  children,
}: {
  title: string;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <Button type="button" size="icon" variant="ghost" className="h-7 w-7" title={title} onClick={onClick}>
      {children}
    </Button>
  );
}
